import {Component, Object3D, InputComponent, PhysXComponent} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {vec3} from 'gl-matrix';
import {Locomotion} from './locomotion.js';

/**
 * This component snaps the player to a fixed rotation when the trigger is
 * moved left or right.
 *
 * @remarks
 * The component is dependent on an {@link InputComponent} component on the same object.
 */
export class SnapRotate extends Component {
    static TypeName = 'snap-rotate';

    /**
     * The physxObject to a apply a rotation force (angularVelocity) to.
     *
     * @requires PhysXComponent
     * This object requires an {@link PhysXComponent} component.
     */
    @property.object()
    physxObject?: Object3D;

    /** The speed at which the player rotates */
    @property.int(5)
    angularVelocity = 5;

    @property.object({required: true})
    locomotionObject!: Object3D;

    private input?: InputComponent;
    private snapped = false;
    private lowThreshold = 0.2;
    private highThreshold = 0.5;
    private physxComponent?: PhysXComponent;
    private locomotionComponent?: Locomotion;

    start() {
        const tempInput = this.object.getComponent(InputComponent);
        if (!tempInput) {
            throw new Error(
                'The snap-rotate component requires an input component on the same object.'
            );
        }
        this.input = tempInput;

        if (!this.physxObject) {
            console.warn('SnapRotate component requires the physxObject to be set.');
            return;
        }

        const tempPhysxComponent = this.physxObject.getComponent(PhysXComponent);
        if (!tempPhysxComponent) {
            throw new Error('The snap-rotate component requires a PhysxComponent.');
        }
        this.physxComponent = tempPhysxComponent;

        const tempLocomotion = this.locomotionObject.getComponent(Locomotion);
        if (!tempLocomotion) {
            throw new Error(
                `snap-rotate(${this.object.name}): locomotionObject does not have a Locomotion`
            );
        }
        this.locomotionComponent = tempLocomotion;

        this.snapped = false;
    }

    queue = new Array<() => void>();

    update() {
        if (!this.input?.xrInputSource?.gamepad?.axes?.length) {
            return;
        }

        const exec = this.queue.pop();

        if (exec) {
            exec();
            return;
        }

        const currentAxis = this.input.xrInputSource.gamepad.axes[2];

        if (Math.abs(currentAxis) < this.lowThreshold) {
            this.snapped = false;
            return;
        }

        // need to release trigger before snapping again
        if (this.snapped || Math.abs(currentAxis) < this.highThreshold) {
            return;
        }

        const lastHeadPos = vec3.fromValues(0, 0, 0);
        this.physxObject!.getPositionWorld(lastHeadPos);
        const angle = 45;
        if (currentAxis < -this.highThreshold && !this.snapped) {
            this.physxComponent!.kinematic = true;
            this.locomotionComponent!.isRotating = true;
            // queue for the next frames in reversed order (LIFO)
            this.queue.push(() => {
                this.physxComponent!.kinematic = false;
                this.locomotionComponent!.isRotating = false;
            });
            this.queue.push(() => {
                this.physxObject!.rotateAxisAngleDegObject([0, 1, 0], angle);
            });

            this.snapped = true;
        }

        if (currentAxis > this.highThreshold && !this.snapped) {
            this.physxComponent!.kinematic = true;
            this.locomotionComponent!.isRotating = true;
            this.queue.push(() => {
                this.physxComponent!.kinematic = false;
                this.locomotionComponent!.isRotating = false;
            });
            this.queue.push(() => {
                this.physxObject!.rotateAxisAngleDegObject([0, 1, 0], -angle);
            });
            this.snapped = true;
        }
    }
}
